import { useState, useEffect } from 'react';
import { getSupabaseClient } from '../../lib/supabase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordInput } from '@ui';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';

const ERROR_MESSAGES: Record<string, string> = {
  guide_account: 'This email is associated with a guide account. Please use the guide app or sign in with a different email.',
  provision_failed: 'We had trouble setting up your account. Please try again or contact support.',
  lookup_failed: 'We couldn\u2019t verify your account details. Please try again.',
  no_seeker_id: 'We couldn\u2019t find your onboarding information. Please start the sign-up process again.',
};

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Show error banner from redirect query params (e.g. ?error=guide_account)
  useEffect(() => {
    const errorCode = searchParams.get('error');
    if (errorCode && ERROR_MESSAGES[errorCode]) {
      setError(ERROR_MESSAGES[errorCode]);
      // Clean the URL so the error doesn't persist on refresh
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        setError(error.message);
        setLoading(false);
      }
      // Note: The user will be redirected to Google, so we don't need to handle success here
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError('Failed to sign in with Google. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const supabase = getSupabaseClient();

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        const userId = data.user?.id;

        if (userId) {
          const { data: profileRow, error: profileError } = await supabase
            .from('profiles')
            .select('role, blocked')
            .eq('id', userId)
            .maybeSingle();

          if (profileError) {
            console.warn('Failed to check profile status', profileError);
          } else {
            // Check if user is a seeker
            if (profileRow && profileRow.role !== 'seeker') {
              await supabase.auth.signOut();
              setError("Your account exists but you're not registered as a seeker. Are you on the right page?");
              setLoading(false);
              return;
            }

            // Check if blocked
            if (profileRow?.blocked) {
              await supabase.auth.signOut();
              setLoading(false);
              navigate('/account-blocked');
              return;
            }
          }
        }

        // First get the seeker record to find the seeker_id
        const { data: seekerRecord, error: seekerRecordError } = await supabase
          .from('seekers')
          .select('seeker_id')
          .eq('user_id', data.user?.id)
          .single();

        if (seekerRecordError) {
          console.error('Error finding seeker record:', seekerRecordError);
          // If there's no seeker record, they haven't completed onboarding
          navigate('/');
        } else {
          // Now check seeker onboarding status using the seeker_id
          const { data: seeker, error: seekerError } = await supabase
            .from('seeker_onboarding_complete')
            .select('onboarding_completed_at')
            .eq('seeker_id', seekerRecord.seeker_id)
            .single();

          if (seekerError) {
            console.error('Error checking seeker status:', seekerError);
            // If there's an error checking onboarding status, send to onboarding
            navigate('/');
          } else if (seeker?.onboarding_completed_at) {
            // Completed onboarding - check if they have a match
            const { data: match, error: matchError } = await supabase
              .from('matches')
              .select('id')
              .eq('seeker_id', seekerRecord.seeker_id)
              .eq('status', 'active')
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            if (matchError || !match) {
              // No match found, send to matching flow
              navigate('/onboarding/finding-match');
            } else {
              // Has a match, send to home page
              navigate('/home');
            }
          } else {
            // Not completed onboarding, send to landing page
            navigate('/');
          }
        }

        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleBackToLanding = () => {
    navigate('/');
  };

  return (
    <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
      <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
        <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <button
            type="button"
            onClick={handleBackToLanding}
            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FEF6D6]/30 bg-transparent text-xl text-[#FEF6D6] transition hover:bg-[#FEF6D6]/10"
            aria-label="Go back"
          >
            {'\u2190'}
          </button>
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>
        <div className="hidden lg:block">
          <SeekerHeader />
        </div>

        <div className="flex flex-1 flex-col items-center lg:px-[120px] lg:py-16">
          <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center justify-center px-6 lg:px-0 w-full max-w-[480px]">
            <div className="w-full text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#304031]/10">
                <svg className="h-8 w-8 text-[#304031]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
              </div>

              <h1 className="mb-2 font-recoleta text-[28px] leading-[1.1] text-[#304031]">Welcome Back</h1>
              <p className="mb-6 text-sm leading-relaxed text-[#304031]/70">
                Sign in to your account to continue your guided care journey.
              </p>

              <form className="text-left" onSubmit={handleSubmit}>
                {error && (
                  <div className="mb-4 rounded-md border border-red-200 bg-[#FBE2E2] p-3 text-sm text-red-800">
                    <p>
                      {error === 'Invalid login credentials' || error.includes('Invalid') || error.includes('credentials')
                        ? (
                          <>
                            Incorrect email or password. Please check your details and try again.{' '}
                            <a href="/auth/forgot" className="font-semibold underline hover:text-red-900 transition-colors">
                              Forgot password?
                            </a>
                          </>
                        )
                        : error}
                    </p>
                  </div>
                )}

                <label className="mb-1 block text-sm font-medium text-[#304031]">Email Address</label>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mb-4 w-full rounded-lg border border-[#304031]/30 bg-[#FFFCEF] px-3 py-2 text-sm text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-1 focus:ring-[#304031]"
                />

                <div className="mb-6">
                  <div className="mb-1 flex items-center justify-between">
                    <label className="block text-sm font-medium text-[#304031]">Password</label>
                    <button
                      type="button"
                      onClick={() => navigate('/auth/forgot')}
                      className="text-xs text-[#304031]/70 hover:text-[#304031] transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <PasswordInput
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-[#304031]/30 bg-[#FFFCEF] px-3 py-2 text-sm text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-1 focus:ring-[#304031]"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base font-semibold transition ${
                    loading
                      ? 'cursor-not-allowed bg-[#304031]/60 text-[#FEF6D6]/80'
                      : 'bg-[#304031] text-[#FEF6D6] hover:bg-[#304031]/90'
                  } focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]`}
                >
                  {loading ? (
                    <>
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#FEF6D6] border-t-transparent"></div>
                      Signing In...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

              <div className="my-6 flex items-center">
                <div className="flex-1 border-t border-[#304031]/20"></div>
                <span className="px-3 text-xs text-[#304031]/60">or</span>
                <div className="flex-1 border-t border-[#304031]/20"></div>
              </div>

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className={`inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#304031]/20 px-6 py-4 text-base font-semibold transition ${
                  loading
                    ? 'cursor-not-allowed bg-white/60 text-[#304031]/40'
                    : 'bg-white text-[#304031] hover:bg-[#304031]/5'
                } focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]`}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Sign in with Google
              </button>

              <p className="mt-6 text-sm text-[#304031]/70">
                Don't have an account?{' '}
                <button
                  onClick={() => navigate('/')}
                  className="font-semibold text-[#304031] underline-offset-4 hover:underline"
                >
                  Sign up here
                </button>
              </p>
            </div>
          </main>
        </div>
      </div>

      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
}
